/**
 * Version 1 Business Concept Model presets.
 *
 * Each preset is authored as a typed `BusinessConceptModelV1` object and
 * serialized to canonical YAML, so a schema field rename or typo is caught
 * by the TypeScript compiler instead of only surfacing at test time.
 *
 * - `BUSINESS_CONCEPT_MODEL_STARTER_YAML` seeds every custom model created
 *   without a source (the "Add model" drawer flow).
 * - `BUSINESS_CONCEPT_MODEL_SKOS_YAML` and `BUSINESS_CONCEPT_MODEL_OWL_YAML` are
 *   the canonical built-in preset documents.
 */
import * as YAML from 'yaml';
import { BusinessConceptModelV1 } from './schema';

const STARTER_MODEL: BusinessConceptModelV1 = {
  rootConcept: {
    type: 'ex:RootConcept',
    labelPredicate: 'ex:label',
    descriptionPredicate: 'ex:description',
    memberToRootPredicates: ['ex:memberOf'],
  },
  concept: {
    type: 'ex:Concept',
    labelPredicate: 'ex:label',
    descriptionPredicate: 'ex:description',
    hierarchy: { parentPredicates: ['ex:broader'] },
  },
  prefixes: {
    ex: 'https://example.com/type#',
  },
};

const SKOS_MODEL: BusinessConceptModelV1 = {
  rootConcept: {
    type: 'skos:ConceptScheme',
    labelPredicate: 'skos:prefLabel',
    descriptionPredicate: 'skos:prefLabel',
    synonymsPredicate: 'skos:altLabel',
    memberToRootPredicates: ['skos:inScheme'],
  },
  concept: {
    type: 'skos:Concept',
    labelPredicate: 'skos:prefLabel',
    descriptionPredicate: 'skos:definition',
    synonymsPredicate: 'skos:altLabel',
    metadata: {
      notation: { predicate: 'skos:notation', kind: 'literal' },
    },
    hierarchy: {
      childPredicates: ['skos:narrower'],
      parentPredicates: ['skos:broader'],
    },
    relations: {
      related: {
        kind: 'direct',
        predicate: 'skos:related',
        label: 'related',
        'map:reveal': 'manual',
        direction: 'undirected',
        'map:style': 'dotted',
      },
    },
  },
  prefixes: {
    skos: 'http://www.w3.org/2004/02/skos/core#',
  },
};

const OWL_MODEL: BusinessConceptModelV1 = {
  rootConcept: {
    type: 'owl:Ontology',
    labelPredicate: 'rdfs:label',
    descriptionPredicate: 'rdfs:comment',
    memberToRootPredicates: ['rdfs:isDefinedBy'],
  },
  concept: {
    type: 'owl:Class',
    labelPredicate: 'rdfs:label',
    descriptionPredicate: 'rdfs:comment',
    hierarchy: { parentPredicates: ['rdfs:subClassOf'] },
    relations: {
      objectProperties: {
        kind: 'typed',
        type: 'owl:ObjectProperty',
        sourcePredicate: 'rdfs:domain',
        targetPredicate: 'rdfs:range',
        labelPredicate: 'rdfs:label',
        inversePredicate: 'owl:inverseOf',
        'map:reveal': 'expand',
      },
    },
  },
  propertyConcept: {
    type: 'owl:DatatypeProperty',
    labelPredicate: 'rdfs:label',
    descriptionPredicate: 'rdfs:comment',
    sourcePredicate: 'rdfs:domain',
    dataTypePredicate: 'rdfs:range',
    hierarchy: { parentPredicates: ['rdfs:subPropertyOf'] },
    dataTypes: [
      { iri: 'xsd:integer', label: 'Integer' },
      { iri: 'xsd:string', label: 'String' },
      { iri: 'xsd:boolean', label: 'Boolean' },
      { iri: 'xsd:date', label: 'Date' },
    ],
  },
  prefixes: {
    owl: 'http://www.w3.org/2002/07/owl#',
    rdf: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
    rdfs: 'http://www.w3.org/2000/01/rdf-schema#',
    xsd: 'http://www.w3.org/2001/XMLSchema#',
  },
};

function toCanonicalYaml(model: BusinessConceptModelV1): string {
  return YAML.stringify(model, { sortMapEntries: false, lineWidth: 0 });
}

export const BUSINESS_CONCEPT_MODEL_STARTER_YAML =
  toCanonicalYaml(STARTER_MODEL);
export const BUSINESS_CONCEPT_MODEL_SKOS_YAML = toCanonicalYaml(SKOS_MODEL);
export const BUSINESS_CONCEPT_MODEL_OWL_YAML = toCanonicalYaml(OWL_MODEL);
